/**
 * Plafonds, seuils de TVA et trimestres de retraite du micro-entrepreneur.
 *
 * Comme lib/tools/incomeTax.js dont ce module est le voisin : du calcul pur,
 * sans interface, donc testable. Les valeurs viennent du référentiel arbitré
 * (docs/glossaries/micro-entreprise/reference-2026.json) et de nulle part
 * ailleurs, avec leurs nuances, qui sont ici plus importantes que les chiffres.
 *
 * TROIS RÈGLES QUE PRESQUE TOUS LES SIMULATEURS RATENT
 *
 * 1. Le régime micro ne cesse que si le seuil est dépassé DEUX années de suite.
 *    La formule qui circule, « le chiffre d'affaires de N-1 ou N-2 ne doit pas
 *    dépasser le seuil », se lit comme une double condition et fait sortir du
 *    régime des gens qui y sont parfaitement.
 *
 * 2. Le plafond proratisé de la première année ne fait sortir de rien. Le micro
 *    est acquis de plein droit l'année de création ET l'année suivante, faute
 *    de chiffre d'affaires de référence. Le BOFiP donne l'exemple d'une
 *    création au 1er août avec 100 000 € en cinq mois, soit 238 562 € en base
 *    annuelle : micro quand même en N et en N+1.
 *
 * 3. Les seuils de TVA n'ont rien à voir avec ceux du régime micro. Dépasser
 *    85 000 € en prestations n'a jamais fait perdre la micro, et franchir
 *    37 500 € ne fait rien perdre du tout : cela rend redevable de la TVA.
 */

import {
    beneficeMicro,
    comparerVersementLiberatoire,
    impotSurLeRevenu,
} from "./incomeTax.js";

/* ------------------------------------------------------------------------- */
/* Plafonds du régime micro                                                   */
/* ------------------------------------------------------------------------- */

/**
 * Seuils applicables aux années 2026, 2027 et 2028.
 *
 * Ils sont actualisés tous les trois ans, dans la proportion de l'évolution de
 * la première tranche du barème de l'impôt sur le revenu : les republier chaque
 * année serait une erreur de méthode.
 */
export const PLAFONDS = {
    vente: 203100,
    services: 83600,
    bnc: 83600,
    cipav: 83600,
};

/** Plafond global d'une activité mixte, et sa part maximale de services. */
export const PLAFOND_MIXTE = { global: 203100, services: 83600 };

/**
 * Plafond de la première année, au prorata des jours d'existence.
 *
 * Formule de l'Urssaf : (seuil × jours d'existence en N) ÷ 365. Le BOFiP la
 * pose à l'envers, en ramenant le chiffre d'affaires à l'année pleine ; les
 * deux reviennent au même et l'Urssaf est plus lisible.
 *
 * CE PRORATA NE FAIT SORTIR DE RIEN l'année de création : voir statutMicro.
 */
export function plafondProratise(seuil, dateDebut) {
    const debut = new Date(dateDebut);
    const finAnnee = Date.UTC(debut.getUTCFullYear(), 11, 31);
    const jours = Math.floor((finAnnee - Date.UTC(
        debut.getUTCFullYear(), debut.getUTCMonth(), debut.getUTCDate(),
    )) / 86400000) + 1;
    return { jours, plafond: Math.round((seuil * jours) / 365) };
}

/**
 * Le régime micro tient-il encore l'an prochain ?
 *
 * @param anciennete "premiere" (créée cette année), "deuxieme" (créée l'an
 *   dernier) ou "etablie" (deux exercices civils complets derrière soi).
 * @param caN1 chiffre d'affaires de la plus récente des deux années lues.
 * @param caN2 chiffre d'affaires de l'année qui la précède.
 *
 * Les deux années sont celles qui décident du régime de l'année suivante : pour
 * savoir si le micro tient en N+1, on lit N (estimé) et N-1 ; pour l'année en
 * cours, N-1 et N-2. Le composant choisit ce qu'il demande, la règle est la
 * même.
 */
export function statutMicro({ activite, anciennete, caN1 = 0, caN2 = 0 }) {
    const seuil = PLAFONDS[activite];

    // Années 1 et 2 : le micro est acquis de plein droit, il n'existe pas de
    // chiffre d'affaires de référence sur lequel le remettre en cause.
    if (anciennete === "premiere" || anciennete === "deuxieme") {
        return { seuil, acquis: true, sortie: false, depassements: 0 };
    }

    const depassements = (caN1 > seuil ? 1 : 0) + (caN2 > seuil ? 1 : 0);
    return {
        seuil,
        acquis: false,
        // Il faut les DEUX années au-dessus. Sous le seuil une seule des deux
        // suffit à rester au micro.
        sortie: caN1 > seuil && caN2 > seuil,
        depassements,
        marge: Math.max(0, seuil - caN1),
    };
}

/**
 * Activité mixte : les deux conditions doivent être remplies en même temps.
 *
 * Un artisan à 150 000 € dont 100 000 € de main-d'œuvre sort du régime malgré
 * un global très en dessous des 203 100 €.
 */
export function statutMixte({ caVente, caServices, cipav = false }) {
    const global = caVente + caServices;

    // UN AFFILIÉ CIPAV N'A QU'UNE CONDITION, et elle porte sur le total : son
    // plafond global est celui des prestations, activité mixte comprise. Lui
    // appliquer les 203 100 € du régime général lui laisserait croire qu'il
    // peut vendre pour 150 000 € en plus de ses honoraires.
    if (cipav) {
        return {
            global,
            cipav: true,
            plafondGlobal: PLAFONDS.cipav,
            globalDepasse: global > PLAFONDS.cipav,
            servicesDepasse: false,
            conforme: global <= PLAFONDS.cipav,
        };
    }

    return {
        global,
        cipav: false,
        plafondGlobal: PLAFOND_MIXTE.global,
        globalDepasse: global > PLAFOND_MIXTE.global,
        servicesDepasse: caServices > PLAFOND_MIXTE.services,
        conforme: global <= PLAFOND_MIXTE.global && caServices <= PLAFOND_MIXTE.services,
    };
}

/**
 * Le régime micro tient-il, en activité mixte, au vu de DEUX années ?
 *
 * Même règle qu'en activité simple : la sortie ne se produit que si les deux
 * années consécutives sortent des conditions. Une seule année au-dessus ne
 * fait rien, et c'est la nuance que tous les simulateurs oublient.
 *
 * Cette conjonction se composait dans le composant du simulateur de plafonds,
 * seul calcul qui vivait hors de la lib : elle rentre ici pour qu'un harnais
 * puisse la tenir.
 */
export function statutMixteSurDeuxAns({ n1, n, cipav = false }) {
    const anneeN1 = statutMixte({ ...n1, cipav });
    const anneeN = statutMixte({ ...n, cipav });
    return {
        n1: anneeN1,
        n: anneeN,
        // Il faut les DEUX années hors conditions pour sortir du régime.
        sortie: !anneeN1.conforme && !anneeN.conforme,
        // Un dépassement isolé ne fait sortir de rien, et le dire évite
        // d'effrayer quelqu'un qui vient d'avoir une bonne année.
        depassementIsole: anneeN1.conforme !== anneeN.conforme,
    };
}

/* ------------------------------------------------------------------------- */
/* Franchise en base de TVA                                                   */
/* ------------------------------------------------------------------------- */

/**
 * Seuils 2026 de la franchise en base.
 *
 * Le seuil unique à 25 000 € a été voté, reporté, suspendu, puis abandonné par
 * la loi du 3 novembre 2025 : il n'est jamais entré en application, et les
 * seuils différenciés ci-dessous n'ont jamais cessé de s'appliquer.
 */
export const SEUILS_TVA = {
    global: { base: 85000, majore: 93500 },
    services: { base: 37500, majore: 41250 },
    reglementees: { base: 50000, majore: 55000 },
    // Avocats, auteurs et artistes-interprètes ont une seconde franchise pour
    // leurs opérations non réglementées, distincte de celle des prestations
    // de services (article 293 B, I bis du CGI).
    autresOperations: { base: 35000, majore: 38500 },
};

/**
 * Chiffre d'affaires d'une année de création, ramené à l'année pleine.
 *
 * (CA × 365) ÷ jours d'activité de l'année de création. Ce chiffre sert à
 * décider de l'année SUIVANTE, et revient au même que comparer le chiffre
 * d'affaires réel au seuil de base réduit de seuilsTvaAjustes (BOFiP § 295).
 * L'année de création elle-même se juge sur ses propres seuils réduits : voir
 * statutTva.
 *
 * Source : BOI-TVA-DECLA-40-10-10 et Entreprendre-Service-Public F21746, qui
 * concordent. Ni les entreprises saisonnières ni celles dont l'activité est
 * intermittente ne pratiquent cet ajustement.
 */
export function caAnnualise(chiffreAffaires, dateDebut) {
    const { jours } = plafondProratise(0, dateDebut);
    if (!jours || jours >= 365) return chiffreAffaires;
    return Math.round((chiffreAffaires * 365) / jours);
}

/**
 * Les seuils de l'année de création, réduits à proportion de sa durée.
 *
 * BOFiP § 285 et 290 : pour qui démarre en cours d'année, les seuils de la
 * franchise « sont ajustés à proportion de la durée de l'année restant à
 * courir », en jours d'activité rapportés à 365, ceux des avocats, auteurs et
 * artistes-interprètes compris. Lancée le 12 juin, une activité de prestations
 * compte 203 jours : son seuil majoré tombe de 41 250 € à 22 942 €, son seuil
 * de base de 37 500 € à 20 856 €. Une année entière, ou une date illisible,
 * garde les seuils pleins.
 */
export function seuilsTvaAjustes(categorie, dateDebut) {
    const s = SEUILS_TVA[categorie];
    const { jours } = plafondProratise(0, dateDebut);
    if (!jours || jours >= 365) return { base: s.base, majore: s.majore, jours: null };
    return {
        base: plafondProratise(s.base, dateDebut).plafond,
        majore: plafondProratise(s.majore, dateDebut).plafond,
        jours,
    };
}

/**
 * Où en suis-je de la franchise en base ?
 *
 * DEUX DATES D'EFFET, ET ELLES N'ONT RIEN À VOIR :
 *   - le seuil majoré fait basculer LE JOUR MÊME du franchissement ;
 *   - le seuil de base fait basculer au 1er janvier de l'année suivante.
 *
 * C'est le premier qui coûte cher, parce qu'il tombe au milieu d'une année
 * pendant laquelle on a facturé sans TVA.
 *
 * LE PIÈGE DE LA CRÉATION TARDIVE. Quand l'année écoulée était l'année de
 * création, son chiffre d'affaires ne se compare pas brut au seuil de base :
 * il se ramène d'abord à l'année pleine. 37 000 € encaissés en prestations à
 * partir du 1er octobre restent sous les 37 500 €, mais valent 146 793 €
 * annualisés, et la TVA est due dès le 1er janvier. Passer `debutN1` fait
 * jouer cette règle ; `saisonnier` l'écarte, comme le BOFiP le prévoit.
 *
 * L'ANNÉE DE CRÉATION ELLE-MÊME A DES SEUILS RÉDUITS. Passer `debutEnCours`
 * juge le chiffre d'affaires de cette année sur les seuils de
 * seuilsTvaAjustes : au-delà du majoré réduit, la TVA est due dès la date du
 * dépassement ; au-delà du seuil de base réduit, au 1er janvier suivant (BOFiP
 * § 290 et 295). `saisonnier` écarte aussi cet ajustement.
 */
export function statutTva({
    categorie,
    caN1 = 0,
    caEnCours = 0,
    debutN1 = null,
    debutEnCours = null,
    saisonnier = false,
}) {
    const s = SEUILS_TVA[categorie];

    // Le chiffre d'affaires de l'an dernier, tel que l'administration le lit.
    const annualise = debutN1 && !saisonnier;
    const caN1Retenu = annualise ? caAnnualise(caN1, debutN1) : caN1;
    const annualisation = annualise
        ? { applique: true, caReel: caN1, caAnnualise: caN1Retenu, jours: plafondProratise(0, debutN1).jours }
        : { applique: false, caReel: caN1, caAnnualise: caN1, jours: null };

    // Les seuils de cette année : pleins, ou réduits quand elle est l'année de
    // création. Sans cette réduction, une activité lancée le 12 juin se croyait
    // en franchise jusqu'à 41 250 €, alors qu'elle doit la TVA dès 22 942 €.
    const reduits = debutEnCours && !saisonnier ? seuilsTvaAjustes(categorie, debutEnCours) : null;
    const prorata = reduits?.jours
        ? { applique: true, jours: reduits.jours, base: reduits.base, majore: reduits.majore, seuilsPleins: s }
        : { applique: false, jours: null };
    const n = prorata.applique ? { base: reduits.base, majore: reduits.majore } : s;

    // L'an dernier décide avant cette année : qui a dépassé le seuil de base en
    // N-1 est redevable depuis le 1er janvier, et franchir le seuil majoré en
    // cours d'année ne change plus rien pour lui. Tester le majoré d'abord
    // annonçait « depuis le jour du franchissement » à quelqu'un qui devait la
    // TVA depuis huit mois.
    if (caN1Retenu > s.base) {
        return {
            ...s,
            statut: "assujetti_depuis_janvier",
            depassementBase: caN1Retenu - s.base,
            annualisation,
            prorata,
        };
    }
    if (caEnCours > n.majore) {
        return {
            ...n,
            statut: "assujetti_immediat",
            depassement: caEnCours - n.majore,
            annualisation,
            prorata,
        };
    }
    if (caEnCours > n.base) {
        return {
            ...n,
            statut: "bascule_janvier_prochain",
            depassementBase: caEnCours - n.base,
            avantMajore: n.majore - caEnCours,
            annualisation,
            prorata,
        };
    }
    return {
        ...n,
        statut: "franchise",
        avantBase: n.base - caEnCours,
        avantMajore: n.majore - caEnCours,
        annualisation,
        prorata,
    };
}

/**
 * Franchise en base quand on vend ET qu'on preste.
 *
 * DEUX CONDITIONS, ET AUCUNE NE DISPENSE DE L'AUTRE. Le seuil global de
 * 85 000 € s'apprécie sur le chiffre d'affaires TOTAL, prestations comprises ;
 * le sous-plafond de 37 500 € s'apprécie sur la seule part prestations. Un
 * micro à 80 000 € de ventes et 10 000 € de prestations respecte les 37 500 €
 * et perd pourtant la franchise, parce que son total fait 90 000 €.
 *
 * L'outil n'offrait que trois catégories exclusives : un mixte devait en
 * choisir une, et se trompait dans les deux sens. En prenant « vente », il
 * ignorait le sous-plafond des prestations ; en prenant « prestations », il
 * ignorait que ses ventes comptent aussi.
 *
 * Le statut rendu est le PLUS SÉVÈRE des deux, parce que la franchise tombe dès
 * qu'une des deux conditions cède.
 */
const SEVERITE = {
    franchise: 0,
    bascule_janvier_prochain: 1,
    assujetti_immediat: 2,
    assujetti_depuis_janvier: 3,
};

export function statutTvaMixte({
    caVenteN1 = 0,
    caServicesN1 = 0,
    caVenteEnCours = 0,
    caServicesEnCours = 0,
    debutN1 = null,
    debutEnCours = null,
    saisonnier = false,
}) {
    const surLeTotal = statutTva({
        categorie: "global",
        caN1: caVenteN1 + caServicesN1,
        caEnCours: caVenteEnCours + caServicesEnCours,
        debutN1,
        debutEnCours,
        saisonnier,
    });
    const surLesPrestations = statutTva({
        categorie: "services",
        caN1: caServicesN1,
        caEnCours: caServicesEnCours,
        debutN1,
        debutEnCours,
        saisonnier,
    });

    const parLesPrestations =
        SEVERITE[surLesPrestations.statut] > SEVERITE[surLeTotal.statut];

    return {
        ...(parLesPrestations ? surLesPrestations : surLeTotal),
        // Laquelle des deux conditions a cédé : c'est ce que le lecteur doit
        // comprendre, et c'est ce qu'aucun simulateur ne lui dit.
        condition: parLesPrestations ? "prestations" : "total",
        surLeTotal,
        surLesPrestations,
    };
}

/* ------------------------------------------------------------------------- */
/* Trimestres de retraite                                                     */
/* ------------------------------------------------------------------------- */

/** SMIC horaire brut au 1er janvier 2026, celui qui sert de référence. */
export const SMIC_HORAIRE_REFERENCE = 12.02;

/** Un trimestre par tranche de revenu égale à 150 heures de SMIC. */
export const HEURES_PAR_TRIMESTRE = 150;

/**
 * Revenu cotisé qui valide un trimestre en 2026.
 *
 * 150 × 12,02 = 1 803 €, que Service-Public écrit désormais en toutes lettres
 * (F23369 : « 1 803,00 »). Le SMIC passé à 12,31 € au 1er juin ne rejoue pas
 * sur les trimestres de l'année en cours.
 */
export const REVENU_PAR_TRIMESTRE = Math.round(HEURES_PAR_TRIMESTRE * SMIC_HORAIRE_REFERENCE);

/**
 * La part du taux global de cotisations qui va à la retraite de base, en 2026
 * (référentiel, repartition_2026_bic et repartition_2026_bnc). La Cipav n'en a
 * pas besoin : ses trimestres suivent un barème en chiffre d'affaires.
 */
export const PART_RETRAITE_BASE = { vente: 0.4345, services: 0.4345, bnc: 0.464 };

/**
 * Le taux de la cotisation de retraite de base des indépendants, 17,87 % en
 * 2026 : c'est lui qui reconvertit une cotisation en revenu cotisé.
 */
export const TAUX_RETRAITE_BASE = 0.1787;

/**
 * La part du chiffre d'affaires qui devient du revenu cotisé pour la retraite.
 *
 * LA MÉTHODE DE L'ADMINISTRATION, PAS L'ABATTEMENT. Service-Public (F23369)
 * reconstitue le revenu cotisé à partir des cotisations payées pour la
 * retraite de base, divisées par 17,87 %. Rapporté au chiffre d'affaires, cela
 * donne 29,9 % en vente, 51,5 % en prestations et 66,5 % en libéral. Jusqu'au
 * 25 septembre 2026, ce module prenait le chiffre d'affaires après abattement
 * (29 %, 50 %, 66 %) : il demandait un peu trop, et un verdict pouvait
 * basculer, 3 trimestres annoncés à 14 000 € de prestations quand la méthode
 * officielle en donne 4.
 */
export function partRevenuCotise(activite) {
    return (TAUX_COTISATIONS[activite] * PART_RETRAITE_BASE[activite]) / TAUX_RETRAITE_BASE;
}

/**
 * Chiffre d'affaires 2026 à réaliser pour valider ses trimestres à la Cipav.
 *
 * SEULE catégorie pour laquelle l'Urssaf publie encore un tableau. Ne jamais
 * étendre ces montants aux artisans, commerçants ou libéraux non réglementés.
 */
export const PALIERS_CIPAV = [2792, 5584, 8376, 11168];

/**
 * Combien de trimestres ce chiffre d'affaires valide-t-il ?
 *
 * Deux mécaniques différentes, et c'est voulu : la Cipav a son barème publié en
 * chiffre d'affaires, tous les autres relèvent de la règle des 150 heures de
 * SMIC, qui porte sur le revenu cotisé pour la retraite de base (voir
 * partRevenuCotise). D'où un écart considérable d'une activité à l'autre : il
 * faut encaisser plus de deux fois plus en vente qu'en libéral pour valider
 * les mêmes quatre trimestres.
 *
 * Le résultat est TRONQUÉ, jamais arrondi, et il porte sur les cotisations
 * effectivement payées : un trimestre impayé ne valide rien.
 */
export function trimestresRetraite({ chiffreAffaires, activite }) {
    if (activite === "cipav") {
        const valides = PALIERS_CIPAV.filter((p) => chiffreAffaires >= p).length;
        const suivant = PALIERS_CIPAV[valides];
        return {
            valides,
            regle: "cipav",
            revenuRetenu: null,
            caProchainTrimestre: suivant ?? null,
            caQuatreTrimestres: PALIERS_CIPAV[3],
        };
    }

    const part = partRevenuCotise(activite);
    const revenuRetenu = chiffreAffaires * part;
    const valides = Math.min(4, Math.floor(revenuRetenu / REVENU_PAR_TRIMESTRE));
    const caPour = (n) => Math.ceil((n * REVENU_PAR_TRIMESTRE) / part);

    return {
        valides,
        regle: "smic",
        revenuRetenu,
        caProchainTrimestre: valides >= 4 ? null : caPour(valides + 1),
        caQuatreTrimestres: caPour(4),
    };
}

/* ------------------------------------------------------------------------- */
/* Ce qu'il reste vraiment                                                    */
/* ------------------------------------------------------------------------- */

/**
 * Contribution à la formation professionnelle.
 *
 * Elle S'AJOUTE au taux global de cotisations, sur le même chiffre d'affaires,
 * et l'ACRE ne l'exonère pas. Les simulateurs qui l'oublient annoncent un net
 * trop élevé, d'autant plus qu'elle triple entre un commerçant et un artisan.
 *
 * Elle n'est due qu'à partir du moment où un chiffre d'affaires positif a été
 * déclaré l'année civile précédente : une première année n'en paie pas.
 */
export const TAUX_CFP = { commercant: 0.001, artisan: 0.003, liberal: 0.002 };

/** Taux global de cotisations sociales, hors ACRE, par activité. */
export const TAUX_COTISATIONS = {
    vente: 0.123,
    services: 0.212,
    bnc: 0.256,
    cipav: 0.232,
};

/**
 * Seuil sous lequel les taxes pour frais de chambre consulaire ne sont pas
 * dues. Elles ne le sont pas non plus la première année d'activité, ni jamais
 * pour une activité libérale.
 */
export const SEUIL_TAXE_CHAMBRE = 5000;

/** Le régime fiscal de la Cipav est celui des BNC : seule la cotisation diffère. */
export function activiteFiscale(activite) {
    return activite === "cipav" ? "bnc" : activite;
}

/** Taux de CFP applicable, selon l'activité et l'inscription au répertoire. */
export function tauxCfp(activite, artisan) {
    if (activite === "bnc" || activite === "cipav") return TAUX_CFP.liberal;
    return artisan ? TAUX_CFP.artisan : TAUX_CFP.commercant;
}

/**
 * Ce qu'il reste vraiment, du chiffre d'affaires encaissé au net dans la poche.
 *
 * QUATRE PRÉLÈVEMENTS, ET LA PLUPART DES OUTILS N'EN COMPTENT QU'UN.
 *
 *   1. les cotisations sociales, au taux global de l'activité ;
 *   2. la contribution à la formation professionnelle, qui s'y ajoute et que
 *      l'ACRE n'exonère pas ;
 *   3. les taxes pour frais de chambre, non chiffrées ici faute de taux
 *      confirmés à la source — voir SEUIL_TAXE_CHAMBRE — et dues par les
 *      seuls commerçants et artisans ;
 *   4. l'impôt sur le revenu, qui dépend du foyer entier et non de la seule
 *      activité.
 *
 * L'IMPÔT RETENU EST LE COÛT MARGINAL DE L'ACTIVITÉ, c'est-à-dire ce que le
 * foyer paie en plus du fait de cette activité, et non l'impôt total du foyer.
 * C'est la seule façon d'obtenir un « ce qu'il me reste » interprétable quand
 * il y a un salaire à côté : sans cela, un conjoint bien payé ferait apparaître
 * une micro-entreprise ruineuse.
 */
export function revenuNet({
    chiffreAffaires,
    activite,
    artisan = false,
    optionFiscale = "bareme",
    autresRevenus = 0,
    enCouple = false,
    enfants = 0,
    parentIsole = false,
}) {
    const fiscale = activiteFiscale(activite);
    const foyer = { enCouple, enfants, parentIsole };

    const cotisations = chiffreAffaires * TAUX_COTISATIONS[activite];
    const cfp = chiffreAffaires * tauxCfp(activite, artisan);

    const comparaison = comparerVersementLiberatoire({
        chiffreAffaires,
        activite: fiscale,
        autresRevenus,
        enCouple,
        enfants,
        parentIsole,
    });

    // Ce que le foyer paierait sans cette activité : la différence est le coût
    // fiscal réel de la micro-entreprise.
    const impotSansActivite = impotSurLeRevenu(autresRevenus, foyer);
    const impotTotal =
        optionFiscale === "vfl" ? comparaison.impotVfl : comparaison.impotBareme;
    const impot = Math.max(0, impotTotal - impotSansActivite);

    const prelevements = cotisations + cfp + impot;

    return {
        benefice: beneficeMicro(chiffreAffaires, fiscale),
        cotisations,
        cfp,
        impot,
        impotTotalFoyer: impotTotal,
        prelevements,
        net: chiffreAffaires - prelevements,
        tauxPrelevement: chiffreAffaires > 0 ? prelevements / chiffreAffaires : 0,
        // Ce que l'autre option fiscale donnerait, pour dire laquelle gagne
        // sans obliger à refaire la simulation.
        netAutreOption:
            chiffreAffaires -
            cotisations -
            cfp -
            Math.max(
                0,
                (optionFiscale === "vfl"
                    ? comparaison.impotBareme
                    : comparaison.impotVfl) - impotSansActivite,
            ),
        // Les taxes de chambre ne visent que les commerçants (TCCI) et les
        // artisans (taxe CMA). Une activité exclusivement non commerciale est
        // exonérée de TCCI (BOI-IF-AUT-10) et ne relève d'aucune chambre de
        // métiers : un libéral, Cipav comprise, n'en paie pas.
        taxeChambreDue: activiteFiscale(activite) !== "bnc" && chiffreAffaires > SEUIL_TAXE_CHAMBRE,
    };
}
